import * as React from 'react';
import { codes } from 'currency-codes';
import {
  Text,
  NumberField,
  Checkbox,
  Select,
  TerritorySelect,
} from '../components/forms';
import Price from '../components/forms/Price';
import Page from '../components/Page';
import Section from '../components/Section';
import Box from '../components/Box';
import { ButtonStyle } from '../components/Button';
import { ActionBar, ActionBarStyle } from '../components/ActionBar';
import { ShippingMethod } from '../shipping-method';
import { ErrorFlash, SuccessFlash } from '../components/Flash';
import { ProfileContext, ProfileManager } from '../profile';
import { useRequestAsync, getHeaders } from '../api/client';

type Props = {
  method: ShippingMethod;
};

const update = (context: ProfileManager) =>
  useRequestAsync((data: ShippingMethod) => ({
    url: data.id
      ? `/store/shipping-methods/${data.id}`
      : '/store/shipping-methods/new',
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

const setValue = (obj: ShippingMethod, onChange: Function) => (
  field: string,
  newValue: any,
) =>
  onChange(
    Object.assign({}, obj, {
      [field]: newValue,
    }),
  );

export default function EditShippingMethod(props: Props) {
  const context = React.useContext(ProfileContext);
  const [method, setMethod] = React.useState(props.method);

  const [state, submit] = update(context);

  const handleFormSubmit = () =>
    submit({
      ...props.method,
      ...method,
    });

  return (
    <Page title={method.name || 'A new method'} subtitle="Edit method">
      {state.error && <ErrorFlash error={state.error} />}
      {state.complete && state.data && (
        <SuccessFlash text="ShippingMethod has been saved" />
      )}
      <form onSubmit={handleFormSubmit} id="method">
        <Section title="Basic details">
          <div className="flex flex-wrap">
            <div className="flex-auto w-50-ns w-100 pr2">
              <Text
                label="Name"
                field={['name']}
                id="name"
                defaultValue={method.name}
                onValueChange={setValue(method, setMethod)}
              />
              <NumberField
                label="Esitmated Delivery Time (in days)"
                field={['delivery_time_days']}
                id="delivery_time_days"
                defaultValue={method.delivery_time_days}
                onValueChange={setValue(method, setMethod)}
              />
              <Checkbox
                label="FREE Shipping?"
                field={['is_free']}
                id="free"
                defaultValue={method.is_free}
                onValueChange={setValue(method, setMethod)}
              />
            </div>
            <div className="flex-auto w-50-ns w-100 pl2">
              <Price
                label="Price (With decimal point i.e. 8.00)"
                field={['price_amount']}
                id="price_amount"
                defaultValue={method.price_amount}
                onValueChange={setValue(method, setMethod)}
              />
              <Select
                label="Currency"
                field={['price_currency']}
                id="price_currency"
                options={codes().map((value: string) => ({
                  value,
                  label: value,
                }))}
                defaultValue={method.price_currency}
                onValueChange={setValue(method, setMethod)}
              />
              <p className="lh-copy mb4 gold b ttu">
                Effetive price: {(method.price_amount / 100).toFixed(2)}{' '}
                {method.price_currency}
              </p>
            </div>
          </div>
        </Section>
        <Section title="Requirements">
          <div className="flex flex-wrap">
            <div className="flex-auto w-50-ns w-100 pr2">
              <NumberField
                label="Minimum Weight (in grams)"
                field={['min_weight']}
                id="min_weight"
                defaultValue={method.min_weight}
                onValueChange={setValue(method, setMethod)}
              />
              <NumberField
                label="Maximum Weight (in grams)"
                field={['max_weight']}
                id="max_weight"
                defaultValue={method.max_weight}
                onValueChange={setValue(method, setMethod)}
              />
            </div>
            <div className="flex-auto w-50-ns w-100 pl2">
              <NumberField
                label="Minimum Subtotal (in specified currency)"
                field={['min_subtotal']}
                id="min_subtotal"
                defaultValue={method.min_subtotal}
                onValueChange={setValue(method, setMethod)}
              />
              <NumberField
                label="Maximum Subtotal (in specified currency)"
                field={['max_subtotal']}
                id="max_subtotal"
                defaultValue={method.max_subtotal}
                onValueChange={setValue(method, setMethod)}
              />
            </div>
          </div>
        </Section>
        <Section title="Availability">
          <div className="flex flex-wrap">
            <div className="flex-auto w-50-ns w-100 pr2">
              <Checkbox
                label="Active?"
                field={['available']}
                id="available"
                defaultValue={method.available}
                onValueChange={setValue(method, setMethod)}
                tooltip="Activate this shipping option for Filtru users"
              />
            </div>
            <div className="flex-auto w-50-ns w-100 pl2">
              <Checkbox
                label="Available Worldwide?"
                field={['available_worldwide']}
                id="available_worldwide"
                defaultValue={method.available_worldwide}
                onValueChange={setValue(method, setMethod)}
                tooltip="Shipping to anywhere in the world "
              />
              {!method.available_worldwide && (
                <TerritorySelect
                  label="Shipping to"
                  field={['available_in']}
                  id="available_in"
                  defaultValue={method.available_in}
                  onValueChange={setValue(method, setMethod)}
                />
              )}
            </div>
          </div>
        </Section>
        <ActionBar
          className={ActionBarStyle.alignCenter}
          actions={[
            {
              className: ButtonStyle.Confirm,
              label: 'Save',
              handler: handleFormSubmit,
            },
          ]}
        />
      </form>
    </Page>
  );
}
